/**
 * components/email/emailApi.ts — small shared toolkit for the Business
 * Email v2 customer surfaces (Connect page, Usage, Groups, Delivery log,
 * Deliverability, Password policy).
 *
 * Mirrors the `api()` helper the pages already use: bearer token from
 * localStorage, JSON in/out, throws an ApiError carrying the HTTP status
 * on !ok so callers can special-case 400/409/423/429.
 */
import { useEffect, useRef, useState } from 'react';
import { getApiUrl } from '../../config/environment';

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export const token = () => localStorage.getItem('token');

export async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(getApiUrl(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token()}`,
      ...(init.headers || {}),
    },
  });
  const body: any = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new ApiError(body?.error || body?.message || `Request failed (HTTP ${r.status})`, r.status);
  }
  return body as T;
}

/**
 * Fetch a binary (e.g. a .mobileconfig profile) with the bearer header
 * and hand back a Blob. The api() helper is header-auth only, so a plain
 * <a href> would arrive unauthenticated — we go through fetch instead.
 */
export async function apiBlob(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const r = await fetch(getApiUrl(path), {
    headers: { Authorization: `Bearer ${token()}` },
  });
  if (!r.ok) {
    const body: any = await r.json().catch(() => ({}));
    throw new ApiError(body?.error || body?.message || `Download failed (HTTP ${r.status})`, r.status);
  }
  const cd = r.headers.get('content-disposition') || '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  return { blob: await r.blob(), filename: m ? decodeURIComponent(m[1]) : null };
}

/** Trigger a browser download for a Blob via a temporary object URL. */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Map an API failure to a message the customer can act on. */
export const friendlyError = (e: any, fallback: string): string => {
  const status = e?.status;
  const msg: string = e?.message || '';
  if (status === 409) return msg || 'That address already exists — pick a different name.';
  if (status === 423) return msg || 'This subscription is suspended or cancelled, so changes aren\'t allowed right now. Check Billing or contact support.';
  if (status === 429) return msg || 'Too many requests — please wait a few minutes and try again.';
  if (status === 400) return msg || fallback;
  return msg || fallback;
};

/** Close-on-Escape for modals. */
export function useEscape(onClose: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
}

/**
 * Visible-only polling: runs `fn` every `ms` while the tab is visible and
 * once more when it becomes visible again. `enabled=false` pauses it.
 */
export function useVisiblePolling(fn: () => void, ms: number, enabled = true) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      ref.current();
    };
    const t = setInterval(tick, ms);
    const onVisible = () => { if (document.visibilityState === 'visible') ref.current(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ms, enabled]);
}

// ---------------------------------------------------------------------------
// Formatting helpers

export const parseDate = (v: string | number | null | undefined): Date | null => {
  if (v === null || v === undefined || v === '') return null;
  const d = typeof v === 'number' ? new Date(v < 1e12 ? v * 1000 : v) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "just now" / "4m ago" / "3h ago" / "2d ago" / "12 Mar 2026". */
export const fmtRelative = (v: string | number | null | undefined, never = 'never'): string => {
  const d = parseDate(v);
  if (!d) return never;
  const diff = Date.now() - d.getTime();
  if (diff < 0) return d.toLocaleString();
  const s = Math.floor(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export const fmtAbsolute = (v: string | number | null | undefined): string => {
  const d = parseDate(v);
  return d ? d.toLocaleString() : '—';
};

/** "340 MB" / "1.2 GB" from a megabyte count. */
export const fmtMb = (mb: number | null | undefined): string => {
  const n = Number(mb);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n >= 1024) {
    const g = n / 1024;
    return `${g >= 10 ? g.toFixed(0) : g.toFixed(1)} GB`;
  }
  return `${Math.round(n)} MB`;
};

export const fmtInt = (n: number | null | undefined): string => {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString() : '—';
};

/** Minimal, pragmatic email check for client-side validation. */
export const isEmail = (s: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || '').trim());

/** Copy text to the clipboard with a fallback for non-secure contexts. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Shared "copied" announcement state for a CopyButton. */
export function useCopy(resetMs = 1800) {
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(null), resetMs);
    return () => clearTimeout(t);
  }, [copied, resetMs]);
  const copy = async (text: string, label?: string) => {
    const ok = await copyText(text);
    setCopied(ok ? (label || text) : null);
    return ok;
  };
  return { copied, copy };
}

// ---------------------------------------------------------------------------
// Business Email v2 — endpoint helpers + wire types
// (everything under /api/email/setup/v2/domains/:id)

export const v2Base = (domainRowId: string) => `/email/setup/v2/domains/${domainRowId}`;

export interface ApiEnvelope<T> { success: boolean; data: T; error?: string }

/** Unwrap {success,data} — throws when success=false so callers get one path. */
export async function apiData<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await api<ApiEnvelope<T>>(path, init);
  if (r && r.success === false) throw new ApiError(r.error || 'Request failed', 200);
  return r?.data as T;
}

export interface MailboxUsage {
  id: number;
  email: string;
  quota_mb: number;
  used_mb: number;
  used_pct: number;
  messages: number;
  last_login_at: string | null;
  last_login_ip: string | null;
  last_login_protocol: string | null;
  active: boolean;
}

export type DeliveryStatus = 'sent' | 'delivered' | 'bounced' | 'deferred' | 'rejected' | string;

export interface DeliveryEvent {
  ts: string | number;
  queue_id: string | null;
  direction: 'inbound' | 'outbound' | string | null;
  from: string | null;
  to: string | null;
  status: DeliveryStatus;
  relay: string | null;
  detail: string | null;
}

export interface GroupDestination { alias_id: number; destination: string; active: boolean }
export interface MailGroup { source_email: string; destinations: GroupDestination[]; is_group: boolean }

export interface PasswordPolicy {
  min_length: number;
  max_length: number;
  classes_required: number;
  rules: string[];
}

export interface Deliverability {
  score: 'good' | 'attention' | 'bad';
  reasons: string[];
  dnsbl: Array<{ zone: string; listed: boolean | 'unknown' }>;
  rdns_ok: boolean;
  records: { mx: boolean; spf: boolean; dkim: boolean; dmarc: boolean };
  checked_at?: string | null;
}

export const fetchUsage = (id: string) => apiData<MailboxUsage[]>(`${v2Base(id)}/mailboxes/usage`);
export const fetchDeliveryLog = (id: string, lines = 200) => apiData<DeliveryEvent[]>(`${v2Base(id)}/delivery-log?lines=${lines}`);
export const fetchGroups = (id: string) => apiData<MailGroup[]>(`${v2Base(id)}/groups`);
export const fetchPasswordPolicy = (id: string) => apiData<PasswordPolicy>(`${v2Base(id)}/password-policy`);
export const fetchDeliverability = (id: string) => apiData<Deliverability>(`${v2Base(id)}/deliverability`);

/** Sum of used/quota across mailboxes for the domain-level total. */
export function sumUsage(rows: MailboxUsage[] | null | undefined) {
  const list = Array.isArray(rows) ? rows : [];
  const used = list.reduce((a, r) => a + (Number(r.used_mb) || 0), 0);
  const quota = list.reduce((a, r) => a + (Number(r.quota_mb) || 0), 0);
  const messages = list.reduce((a, r) => a + (Number(r.messages) || 0), 0);
  return { used, quota, messages, pct: quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0 };
}
